import { useEffect, useState } from 'react';

import { Box, Button, ButtonGroup, Heading, Modal, Text, TextButton } from '@zillow/constellation-10';

import { formatDate } from '@common/paymentsUtils/dateUtils';
import type { RenterMoveInCostsDetailsInterface, RenterPaymentSubscriptionInterface } from '@renter-hub/models/Types';
import { MOVE_IN_COSTS_PAYMENT, RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';

interface Props {
  onBack: () => void;
  paymentSubscriptionDetails: RenterPaymentSubscriptionInterface;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  typeOfPaymentToMake?: typeof RENT_PAYMENT | typeof MOVE_IN_COSTS_PAYMENT;
  updatePaymentMethod: (paymentMethodId: string) => void;
  onAddNewPaymentClick: () => void;
  newPaymentMethodId: string;
}
const UpdatePaymentMethodsPage = ({
  paymentSubscriptionDetails,
  moveInCostsDetails,
  onBack,
  typeOfPaymentToMake = RENT_PAYMENT,
  updatePaymentMethod,
  onAddNewPaymentClick,
  newPaymentMethodId,
}: Props) => {
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (newPaymentMethodId) {
      setSelectedPaymentMethodId(newPaymentMethodId);
    } else if (typeOfPaymentToMake === MOVE_IN_COSTS_PAYMENT) {
      setSelectedPaymentMethodId(moveInCostsDetails.paymentMethodId || '');
    } else {
      setSelectedPaymentMethodId(paymentSubscriptionDetails.paymentMethodId || '');
    }
  }, []);

  const renderPaymentDateText = () => {
    const date =
      typeOfPaymentToMake === RENT_PAYMENT ? paymentSubscriptionDetails.nextScheduledDate : moveInCostsDetails.sendDate;

    const subHeaderText =
      typeOfPaymentToMake === RENT_PAYMENT && paymentSubscriptionDetails.recurring ? (
        <Text>Future payments will be processed on the {formatDate(date, 'do')} of the month.</Text>
      ) : (
        <Text>
          Your payment will be processed on <strong>{formatDate(date, 'MMMM d, yyyy')}</strong>.
        </Text>
      );

    return subHeaderText;
  };

  const isButtonDisabled = () => {
    const paymentMethodIdToCheck =
      typeOfPaymentToMake === MOVE_IN_COSTS_PAYMENT
        ? moveInCostsDetails.paymentMethodId
        : paymentSubscriptionDetails.paymentMethodId;

    return selectedPaymentMethodId === paymentMethodIdToCheck;
  };
  return (
    <Modal
      open={true}
      trigger={null}
      size="md"
      header={<Heading level={2}>Select a payment method</Heading>}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onBack();
        }
      }}
      body={
        <>
          <Box css={{ marginBottom: 'default' }}>{renderPaymentDateText()}</Box>
          <AvailablePaymentMethods
            selectedPaymentMethodId={selectedPaymentMethodId}
            onPaymentMethodClick={(paymentMethod) => setSelectedPaymentMethodId(paymentMethod.id)}
            renderRadioView={true}
          />
        </>
      }
      footer={
        <ButtonGroup aria-label="Modal actions">
          <TextButton textStyle="body-bold" onClick={onAddNewPaymentClick} css={{ alignItems: 'center' }}>
            Add new payment method
          </TextButton>
          <Button
            disabled={isButtonDisabled()}
            emphasis="filled"
            onClick={async () => {
              if (selectedPaymentMethodId) {
                setIsLoading(true);
                await updatePaymentMethod(selectedPaymentMethodId);
                setIsLoading(false);
              }
            }}
            loading={isLoading}
          >
            Update
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default UpdatePaymentMethodsPage;
